import { createStore } from '@/utils/createStore'

/**
 * Local row shape for the Agreement store.
 *
 * Unlike the `Agreement` type in `@/types` (which uses `Date` objects), this
 * interface keeps all dates as plain 'YYYY-MM-DD' STRINGS so records round-trip
 * cleanly through localStorage/JSON without manual Date parsing.
 *
 * `templateType` and `status` are kept as strings holding the same values as
 * the `AgreementTemplate` / `AgreementStatus` enums (e.g. 'Tripartite Agreement',
 * 'Active') so the List columns' tag/filter logic keeps working.
 */
export interface AgreementRow {
  id: string
  code?: string              // human-readable Agreement Code (AGR###)
  proposalId: string
  leadId: string
  agreementType: string[]   // from AgreementType master (e.g. PPA, GCA)
  templateType: string
  status: string
  generatedAt: string
  signedAt: string | null
  startDate: string          // the Agreement Date (date the agreement was entered into / executed)
  endDate: string
  validityDays?: number      // contract validity period, in days
  renewalReminder: number
  stampPaperValue?: number   // ₹ denomination the agreement is executed on
  stampPaperNumber?: string  // stamp paper's own reference/serial number
  placeOfExecution?: string
  notes?: string             // special clauses / free-form notes
  parties?: {
    role: string
    name: string
    rep?: string
    designation?: string
    email?: string
    phone?: string
    address?: string
  }[]
  partyName?: string // Keeping for backward compatibility
  signedCopyUrl?: string
  // One uploaded signed copy per agreement type (PPA / GCA / BA …).
  signedCopies?: { type: string; fileName: string }[]
  // Amendments raised against this agreement (dates as YYYY-MM-DD strings).
  amendments?: {
    id: string
    revisionNumber: number
    changeDescription: string
    validFrom?: string
    validTo?: string
    signedCopyUrl?: string
    signedAt?: string
    createdAt: string
  }[]
  createdAt: string
  updatedAt: string
}

export const agreementStore = createStore<AgreementRow>('sp_agreements', [], 'A', 3, '/agreements')
